import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";

type ActionDialogProps = {
  title: string;
  description: React.ReactNode;
  show: boolean;
  confirmText: string;
  /**
   * The confirming button's colour, by what the act does. Red by default, because most
   * confirmations here take something away. An act that gives something back - publishing a quote,
   * reopening an order - passes "primary", so red keeps meaning what it says.
   */
  confirmColor?: "primary" | "error";
  cancelText?: string;
  onConfirm: () => Promise<void> | void;
  onCancel: () => Promise<void> | void;
};

export function ActionDialog(props: ActionDialogProps) {
  const { title, description, show, onConfirm, onCancel, cancelText, confirmText, confirmColor } = props;
  const [confirming, setConfirming] = useState(false);

  const handleOnCancel = async () => {
    await onCancel();
  };

  const handleOnConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  return show ? (
    <Dialog
      open={true}
      onClose={() => handleOnCancel()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      {/*
        The close control sits beside the title, never inside it. A browser reads a nested button's
        label as part of the heading, so the heading - and the dialog, which names itself from the
        heading through aria-labelledby - ended with the word "close".
      */}
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", pr: 1 }}>
        <DialogTitle id="alert-dialog-title" sx={{ flex: 1 }}>
          {title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => handleOnCancel()}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <DialogContent>
        <DialogContentText id="alert-dialog-description" component="div">
          {description}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant={"outlined"} onClick={() => handleOnCancel()} autoFocus>
          {cancelText || "Cancel"}
        </Button>
        <Button
          variant={"contained"}
          color={confirmColor ?? "error"}
          loading={confirming}
          onClick={() => handleOnConfirm()}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;
}
